import React, { useEffect, useState } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { TID } from "@/lib/testIds";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function Support() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ category: "audience", subject: "", body: "" });

  const load = async () => {
    const { data } = await api.get("/support/tickets");
    setTickets(data.items);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openTicket = async (t) => {
    setSelected(t);
    const { data } = await api.get(`/support/tickets/${t.ticket_id}`);
    setMessages(data.messages || []);
  };
  const send = async () => {
    if (!reply.trim() || !selected) return;
    await api.post(`/support/tickets/${selected.ticket_id}/reply`, { body: reply });
    setReply("");
    openTicket(selected);
  };
  const create = async () => {
    if (!form.subject) { toast.error("Add a subject"); return; }
    await api.post("/support/tickets", form);
    toast.success("Ticket created");
    setShowNew(false); setForm({ category: "audience", subject: "", body: "" });
    load();
  };

  return (
    <div className="App min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-2">Support</div>
            <h1 className="font-heading font-black tracking-tighter text-4xl md:text-5xl">Tickets.</h1>
          </div>
          <Button data-testid={TID.support.newBtn} onClick={() => setShowNew(!showNew)} className="rounded-sm">New ticket</Button>
        </div>

        {showNew && (
          <div className="border border-border bg-white p-6 mb-6 space-y-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid={TID.support.category} className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="audience">Audience request question</SelectItem>
                  <SelectItem value="quote">Quote question</SelectItem>
                  <SelectItem value="payment">Payment issue</SelectItem>
                  <SelectItem value="delivery">Delivery issue</SelectItem>
                  <SelectItem value="replacement">Replacement request</SelectItem>
                  <SelectItem value="account">Account issue</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Subject</Label><Input data-testid={TID.support.subject} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1" /></div>
            <div><Label>Message</Label><Textarea data-testid={TID.support.body} rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1" /></div>
            <Button data-testid={TID.support.submit} onClick={create} className="rounded-sm">Create ticket</Button>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-1 border border-border bg-white">
            <div className="p-3 border-b border-border font-semibold text-sm">Tickets ({tickets.length})</div>
            <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
              {tickets.map((t) => (
                <button
                  key={t.ticket_id}
                  data-testid={`support-ticket-${t.ticket_id}`}
                  onClick={() => openTicket(t)}
                  className={`w-full text-left p-3 hover:bg-muted ${selected?.ticket_id === t.ticket_id ? "bg-muted" : ""}`}
                >
                  <div className="font-mono text-[10px] text-muted-foreground">{t.reference}</div>
                  <div className="text-sm font-semibold truncate">{t.subject}</div>
                  <div className="text-xs text-muted-foreground">{t.category}</div>
                </button>
              ))}
              {tickets.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">No tickets yet.</div>}
            </div>
          </div>

          <div className="md:col-span-2 border border-border bg-white min-h-[400px] flex flex-col">
            {!selected && <div className="flex-1 grid place-items-center text-sm text-muted-foreground">Select a ticket to view messages.</div>}
            {selected && (
              <>
                <div className="p-4 border-b border-border">
                  <div className="font-mono text-[11px] text-muted-foreground">{selected.reference}</div>
                  <div className="font-semibold">{selected.subject}</div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((m) => (
                    <div key={m.message_id} className={`${m.from_role === "customer" ? "text-right" : "text-left"}`}>
                      <div className={`inline-block max-w-[85%] px-3 py-2 rounded-sm border text-sm ${m.from_role === "customer" ? "bg-primary/5 border-primary/20" : "bg-muted border-border"}`}>
                        <div className="text-[10px] font-mono uppercase tracking-widest-2 text-muted-foreground mb-1">{m.from_role}</div>
                        {m.body}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-3 border-t border-border flex gap-2">
                  <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply…" className="text-sm" data-testid="support-reply-input" />
                  <Button onClick={send} className="rounded-sm" data-testid="support-reply-send">Send</Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
